import type { FC } from 'react';
import type {
  VendorQuote,
  VolumeCalculation,
  PrintCalculation,
} from '../lib/types';
import { formatINR } from '../lib/constants';
import { s } from '../lib/styles';

interface CostChipProps {
  label: string;
  value: string;
  color: string;
}

const CostChip: FC<CostChipProps> = ({ label, value, color }) => (
  <div
    style={{
      background: '#f8fafc',
      border: '1px solid #f1f5f9',
      borderRadius: 7,
      padding: '6px 9px',
    }}
  >
    <div
      style={{
        fontSize: 9,
        color: '#94a3b8',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 3,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 12, fontWeight: 700, color }}>{value}</div>
  </div>
);

interface Props {
  quote: VendorQuote;
  index: number;
  isRecommended: boolean;
  isSelected: boolean;
  onToggleSelect: () => void;
  quantity: string;
  volumeCalc: VolumeCalculation | undefined;
  printCalc: PrintCalculation | undefined;
}

const VendorQuoteCard: FC<Props> = ({
  quote,
  index,
  isRecommended,
  isSelected,
  onToggleSelect,
  quantity,
  volumeCalc,
  printCalc,
}) => {
  const sameCity = quote.distance_km === 0;

  return (
    <div
      onClick={onToggleSelect}
      style={{
        ...s.vendorCard,
        borderColor: isSelected
          ? '#2563eb'
          : isRecommended
            ? '#93c5fd'
            : '#e2e8f0',
        background: isSelected
          ? '#eff6ff'
          : isRecommended
            ? '#f8faff'
            : '#ffffff',
        cursor: 'pointer',
      }}
    >
      {/* Checkbox + name + badge */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <div
            style={{
              width: 18,
              height: 18,
              borderRadius: 4,
              flexShrink: 0,
              border: `2px solid ${isSelected ? '#2563eb' : '#cbd5e1'}`,
              background: isSelected ? '#2563eb' : '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
            }}
          >
            {isSelected && (
              <span
                style={{
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 800,
                  lineHeight: 1,
                }}
              >
                ✓
              </span>
            )}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              {quote.name}
            </div>
            <div style={{ fontSize: 10, color: '#64748b' }}>
              📍 {quote.factory_location} · ⭐ {quote.rating} · ID: {quote.id}
            </div>
          </div>
        </div>
        {isRecommended && <span style={s.bestBadge}>✦ Recommended</span>}
      </div>

      {/* Material cost chips */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 7,
          marginBottom: 10,
        }}
      >
        <CostChip
          label="Paper rate"
          value={`₹${quote.paper_rate_per_kg}/kg`}
          color="#2563eb"
        />
        <CostChip
          label="Conversion"
          value={`₹${quote.conversion_cost_per_kg}/kg`}
          color="#d97706"
        />
        <CostChip
          label="Material/unit"
          value={formatINR(quote.material_cost_per_unit)}
          color="#7c3aed"
        />
      </div>

      {/* Shipping */}
      <div
        style={{
          background: sameCity ? '#f0fdf4' : '#f8fafc',
          border: `1px solid ${sameCity ? '#bbf7d0' : '#f1f5f9'}`,
          borderRadius: 8,
          padding: '8px 12px',
          marginBottom: 8,
        }}
      >
        <div
          style={{
            fontSize: 9,
            fontWeight: 700,
            color: '#94a3b8',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            marginBottom: 6,
          }}
        >
          🚛 Shipping {sameCity ? '(same city — free)' : ''}
        </div>
        {sameCity ? (
          <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>
            ₹0 — vendor is in {quote.factory_location}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 11, color: '#475569' }}>
              {volumeCalc?.selected_vehicle}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>·</span>
            <span style={{ fontSize: 11, color: '#475569' }}>
              {volumeCalc?.total_volume_m3?.toFixed(2)} m³
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>·</span>
            <span style={{ fontSize: 11, color: '#475569' }}>
              📏 {quote.distance_km.toLocaleString()} km
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>·</span>
            <span style={{ fontSize: 11, color: '#475569' }}>
              Total:{' '}
              <strong style={{ color: '#dc2626' }}>
                {formatINR(quote.shipping_total)}
              </strong>
            </span>
            <span style={{ fontSize: 11, color: '#475569' }}>
              · /unit:{' '}
              <strong style={{ color: '#dc2626' }}>
                {formatINR(quote.shipping_per_unit)}
              </strong>
            </span>
          </div>
        )}
      </div>

      {/* Printing (hidden when no-print) */}
      {(printCalc?.colour_multiplier ?? 0) > 0 && (
        <div
          style={{
            background: '#faf5ff',
            border: '1px solid #e9d5ff',
            borderRadius: 8,
            padding: '8px 12px',
            marginBottom: 8,
          }}
        >
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 6,
            }}
          >
            🖨 Printing
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <span style={{ fontSize: 11, color: '#475569' }}>
              {printCalc!.colour_label}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>·</span>
            <span style={{ fontSize: 11, color: '#475569' }}>
              ₹{printCalc!.print_rate_per_kg}/kg ×{' '}
              {printCalc!.colour_multiplier}
            </span>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>·</span>
            <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>
              /unit: {formatINR(quote.print_cost_per_unit)}
            </span>
          </div>
        </div>
      )}

      {/* Final price */}
      <div
        style={{
          display: 'flex',
          borderTop: '1px solid #f1f5f9',
          paddingTop: 10,
          marginTop: 2,
        }}
      >
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 9,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 2,
            }}
          >
            Per unit (all-in)
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: isSelected || isRecommended ? '#2563eb' : '#1e293b',
            }}
          >
            {formatINR(quote.price_per_unit)}
          </div>
        </div>
        <div
          style={{ flex: 1, borderLeft: '1px solid #e2e8f0', paddingLeft: 14 }}
        >
          <div
            style={{
              fontSize: 9,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              marginBottom: 2,
            }}
          >
            Total · {Number(quantity).toLocaleString()} units
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: isSelected || isRecommended ? '#7c3aed' : '#64748b',
            }}
          >
            {formatINR(quote.total_price)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorQuoteCard;
